import { config } from '../../config';
import { trace } from './trace';
import { routeStorage } from '../login/storage';
import { reportErrorToSentry } from './sentry';
import type { UserManager } from 'oidc-client-ts';

const saveCurrentRoute = () => {
    const initialRoute = window.location.hash.replace(/^#\/?/u, '');

    if (initialRoute) {
        routeStorage.saveRoute(initialRoute);
        trace('initialRoute: saving initial route', initialRoute);
    }
};

const doSignIn = (userManager: UserManager, isAuthCodeRedirect: boolean) =>
    isAuthCodeRedirect ? userManager.signinCallback() : userManager.signinSilent();

export const attemptInitialSignIn = async (userManager: UserManager) => {
    const isAuthCodeRedirect =
        window.location.href.startsWith(config.login.redirectUri as string) &&
        /[?&]code=[^&]*/.test(window.location.search);

    const searchParams = new URLSearchParams(window.location.search);
    const state = searchParams.get('state');
    if (state !== null && (await userManager.settings.stateStore.get(state)) === null) {
        // Ensure that there is a state stored. In some cases, clearStaleState might remove the state from storage
        // which leads to issues when loading the page
        console.warn(`Signin error: No matching state found in storage. Redirecting to ${window.location.origin}`);

        routeStorage.discardRoute();

        // We want a real page reload, which will either:
        //  - perform a silent signin, which succeeds if the user already has a session from another login
        //  - trigger a new redirection that brings us back here with a valid state value.
        // We use replace here to override the redirect URL and prevent the back button to go
        // back to the redirect page.
        window.location.replace(window.location.origin);

        // The window.location.replace call runs in the background. We do not want
        // to continue with the rest of the authorization flow. This would just lead
        // to a thrown error from signinCallback(), which we would see in the monitoring.
        // Therefore, we block this promise to let the navigation happen.
        await new Promise<never>(() => {});
    }

    try {
        const user = await doSignIn(userManager, isAuthCodeRedirect);
        const initialRoute = routeStorage.getRoute();

        trace('initialRoute: lookup', initialRoute);

        if (isAuthCodeRedirect && initialRoute !== null) {
            trace(`initialRoute: Go to location "${initialRoute}"`);

            window.location.replace(`${window.location.origin}/#/${initialRoute}`);
            // TODO we actually want to use history.replaceState, because location.replace causes page reload
            //  but this currently does not work with our route handling for some reason and the initial route is ignored
            // window.history.replaceState({}, '', `${window.location.origin}/#/${initialRoute}`);
        } else if (isAuthCodeRedirect) {
            window.history.replaceState({}, '', window.location.origin);
        }
        routeStorage.discardRoute();

        return user;
    } catch (error) {
        trace('oidc.signinSilent failed, trying page redirect...', error);

        if (config.login.preventRedirect) {
            console.warn('[feature/login] redirect prevented due to config. Error was', error);
        } else if (isAuthCodeRedirect) {
            trace('oidc.signinSilent.error', 'redirect prevented due to unexpected signin error', error);
            routeStorage.discardRoute();
            reportErrorToSentry(error);
        } else {
            saveCurrentRoute();
            await userManager.signinRedirect();
            // signinRedirect sets window.location - Until the new page is loaded, code will still be executed, and
            // there is no easy way to await the page load.
            // Therefore, we return early here to ensure that no code is executed after the signinRedirect call
            return undefined;
        }

        trace('auth problem?', error);
        return Promise.reject(new Error('Need to sign in'));
    }
};
